(() => {
  'use strict';

  const SELECTORS = {
    booking: '.db-booking',
    panel: '.db-booking__panel',
    date: '#db-event-date',
    location: '#db-event-location',
    eventCard: '.db-booking__event-card',
    flavourGrid: '#db-flavour-grid',
    flavourCard: '.db-booking__flavour-card',
    flavourCount: '#db-flavour-count',
    flavourLimit: '#db-flavour-limit',
    flavourMessage: '#db-flavour-message',
    flavourContinue: '[data-step="5"] [data-next-step="6"]',
    extrasGrid: '#db-extras-grid',
    extraCard: '.db-booking__extra-card',
    extrasTotal: '#db-extras-total',
    firstName: '#db-customer-first-name',
    lastName: '#db-customer-last-name',
    email: '#db-customer-email',
    phone: '#db-customer-phone',
    notes: '#db-customer-notes'
  };

  const FLAVOUR_COLOURS = {
    lemon: '#f2de72',
    mango: '#f0aa36',
    strawberry: '#e98a91',
    'passion-fruit': '#efb83d',
    vanilla: '#f4e4b9',
    'dark-chocolate': '#4a2a20',
    chocolate: '#81513c',
    coffee: '#a77a5c',
    pistachio: '#a9bc82',
    hazelnut: '#ba906c',
    cheesecake: '#f3dcb7',
    bacio: '#8e634e',
    tiramisu: '#bd9775',
    stracciatella: '#e9dfce',
    'mint-chocolate': '#a9d4bb',
    'amarena-cookies': '#c7808d',
    oreo: '#77706b',
    coconut: '#eee9db',
    cremino: '#c69a72'
  };

  const asArray = (value) => (
    Array.isArray(value) ? value : []
  );

  const asNumber = (value, fallback = 0) => {
    const parsed = Number(value);

    return Number.isFinite(parsed)
      ? parsed
      : fallback;
  };

  const formatLocalDate = (date) => {
    const year = date.getFullYear();

    const month = String(
      date.getMonth() + 1
    ).padStart(2, '0');

    const day = String(
      date.getDate()
    ).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  class DolobitesBookingV3 {
    constructor(booking) {
      this.booking = booking;

      this.config =
        window.DOLOBITES_BOOKING_CONFIG || {};

      this.pricing =
        window.DOLOBITES_PRICING || null;

      this.abortController =
        new AbortController();

      this.signal =
        this.abortController.signal;

      this.elements =
        this.collectElements();

      this.state =
        this.createInitialState();
    }

    collectElements() {
      const query = (selector) =>
        this.booking.querySelector(selector);

      const queryAll = (selector) =>
        Array.from(
          this.booking.querySelectorAll(selector)
        );

      return {
        panels: queryAll(SELECTORS.panel),

        date: query(SELECTORS.date),

        location: query(SELECTORS.location),

        eventCards: queryAll(
          SELECTORS.eventCard
        ),

        flavourGrid: query(
          SELECTORS.flavourGrid
        ),

        flavourCount: query(
          SELECTORS.flavourCount
        ),

        flavourLimit: query(
          SELECTORS.flavourLimit
        ),

        flavourMessage: query(
          SELECTORS.flavourMessage
        ),

        flavourContinue: query(
          SELECTORS.flavourContinue
        ),

        extrasGrid: query(
          SELECTORS.extrasGrid
        ),

        extrasTotal: query(
          SELECTORS.extrasTotal
        ),

        firstName: query(
          SELECTORS.firstName
        ),

        lastName: query(
          SELECTORS.lastName
        ),

        email: query(
          SELECTORS.email
        ),

        phone: query(
          SELECTORS.phone
        ),

        notes: query(
          SELECTORS.notes
        )
      };
    }

    createInitialState() {
      const selection =
        window.DOLOBITES_V3_SELECTION || {};

      const selectedEvent =
        this.booking.querySelector(
          `${SELECTORS.eventCard}.is-selected`
        );

      return {
        currentStep:
          this.getActiveStep(),

        date:
          this.elements.date?.value || '',

        eventType:
          selectedEvent?.dataset.eventType || '',

        location:
          this.elements.location?.value.trim() || '',

        guests: selection.guests
  ? asNumber(selection.guests)
  : null,

        selection:
          this.normalizeSelection(selection),

        selectedFlavours: [],

        selectedExtras: [],

        customer:
          this.readCustomer()
      };
    }

    normalizeSelection(selection = {}) {
      const pans =
        asNumber(selection.pans, 0);

      const flavoursIncluded =
        asNumber(
          selection.flavoursIncluded,
          pans
        );

      return {
        ...selection,

        id:
          selection.id ||
          `pans-${pans}`,

        pans,

        guests:
          asNumber(selection.guests, 0),

        name:
          selection.name ||
          ``,

        price:
          asNumber(selection.price, 0),

        flavoursIncluded:
          Math.max(
            flavoursIncluded,
            0
          )
      };
    }

    init() {
      if (!this.pricing) {
        console.error(
          'Dolobites pricing module is missing.'
        );

        return;
      }

      this.setMinimumDate();

      this.bindNavigation();

      this.bindDate();

      this.bindEventType();

      this.bindLocation();

      this.bindCustomer();

      this.bindExternalModules();

      this.renderFlavours();

      this.renderExtras();

      this.restoreSelectionsFromDom();

      this.renderAll();

      /*
       * Step 4 viene caricato come modulo
       * separato. Questo secondo sync
       * acquisisce la sua selezione iniziale
       * senza accoppiare i due moduli.
       */
      window.setTimeout(
        () => this.syncStepFourSelection(),
        0
      );
    }

    destroy() {
      this.abortController.abort();

      delete this.booking.dataset.initialized;
    }

    getActiveStep() {
      const activePanel =
        this.booking.querySelector(
          `${SELECTORS.panel}.is-active`
        );

      return asNumber(
        activePanel?.dataset.step,
        1
      );
    }

    showStep(stepNumber) {
      const targetStep =
        asNumber(stepNumber, 0);

      const activePanel =
        this.elements.panels.find(
          (panel) =>
            asNumber(
              panel.dataset.step,
              0
            ) === targetStep
        );

      if (!activePanel) {
        return;
      }

      this.elements.panels.forEach(
        (panel) => {
          const isActive =
            panel === activePanel;

          panel.classList.toggle(
            'is-active',
            isActive
          );

          panel.setAttribute(
            'aria-hidden',
            String(!isActive)
          );
        }
      );

      this.state.currentStep =
        targetStep;

      this.emit(
        'dolobites:step-change',
        {
          step: targetStep
        }
      );

      window.requestAnimationFrame(
        () => {
          const headerOffset = 120;

          const top =
            activePanel
              .getBoundingClientRect()
              .top +
            window.scrollY -
            headerOffset;

          window.scrollTo({
            top: Math.max(top, 0),
            behavior: 'smooth'
          });
        }
      );
    }

    bindNavigation() {
      this.booking.addEventListener(
        'click',
        (event) => {
          const nextButton =
            event.target.closest(
              '[data-next-step]'
            );

          if (
            nextButton &&
            this.booking.contains(nextButton)
          ) {
            if (nextButton.disabled) {
              return;
            }

            const nextStep =
              asNumber(
                nextButton.dataset.nextStep,
                0
              );

            if (nextStep) {
              this.showStep(nextStep);
            }

            return;
          }

          const previousButton =
            event.target.closest(
              '[data-previous-step]'
            );

          if (
            previousButton &&
            this.booking.contains(
              previousButton
            )
          ) {
            const previousStep =
              asNumber(
                previousButton.dataset
                  .previousStep,
                0
              );

            if (previousStep) {
              this.showStep(previousStep);
            }
          }
        },
        {
          signal: this.signal
        }
      );
    }

    bindDate() {
      const input =
        this.elements.date;

      if (!input) {
        return;
      }

      const update = () => {
        this.state.date =
          input.value;

        this.updateStepButton(
          1,
          Boolean(this.state.date)
        );

        this.emitStateChange('date');
      };

      input.addEventListener(
        'input',
        update,
        {
          signal: this.signal
        }
      );

      input.addEventListener(
        'change',
        update,
        {
          signal: this.signal
        }
      );

      update();
    }

    bindEventType() {
      this.elements.eventCards.forEach(
        (card) => {
          card.addEventListener(
            'click',
            () => {
              this.elements.eventCards
                .forEach((item) => {
                  const selected =
                    item === card;

                  item.classList.toggle(
                    'is-selected',
                    selected
                  );

                  item.setAttribute(
                    'aria-pressed',
                    String(selected)
                  );
                });

              this.state.eventType =
                card.dataset.eventType || '';

              this.updateStepButton(
                2,
                Boolean(
                  this.state.eventType
                )
              );

              this.emitStateChange(
                'eventType'
              );
            },
            {
              signal: this.signal
            }
          );
        }
      );

      this.updateStepButton(
        2,
        Boolean(this.state.eventType)
      );
    }

    bindLocation() {
  const input =
    this.elements.location;

  if (!input) {
    return;
  }

  const update = () => {
    this.state.location =
      input.value.trim();

    const googleLocation =
      window.DOLOBITES_V3_LOCATION;

    const isValidGoogleLocation =
      Boolean(
        googleLocation &&
        googleLocation.serviceable &&
        googleLocation.address ===
          this.state.location
      );

    this.updateStepButton(
      3,
      isValidGoogleLocation
    );

    this.emitStateChange(
      'location'
    );
  };

  input.addEventListener(
    'input',
    update,
    {
      signal: this.signal
    }
  );

  input.addEventListener(
    'change',
    update,
    {
      signal: this.signal
    }
  );

  document.addEventListener(
    'dolobites:location-change',
    update,
    {
      signal: this.signal
    }
  );

  update();
}

    bindCustomer() {
      [
        this.elements.firstName,
        this.elements.lastName,
        this.elements.email,
        this.elements.phone,
        this.elements.notes
      ]
        .filter(Boolean)
        .forEach((input) => {
          input.addEventListener(
            'input',
            () => {
              this.state.customer =
                this.readCustomer();

              this.emitStateChange(
                'customer'
              );
            },
            {
              signal: this.signal
            }
          );
        });
    }

    bindExternalModules() {
      document.addEventListener(
        'dolobites:pans-change',
        (event) => {
          this.syncStepFourSelection(
            event.detail
          );
        },
        {
          signal: this.signal
        }
      );
    }

    syncStepFourSelection(
      detail = null
    ) {
      const source =
        detail ||
        window.DOLOBITES_V3_SELECTION ||
        {};

      const previousLimit =
        this.getFlavourLimit();

      const nextSelection =
        this.normalizeSelection(source);

      this.state.guests =
        asNumber(
          nextSelection.guests,
          asNumber(
            this.booking.querySelector(
              '#db-guest-range-v3'
            )?.value,
            40
          )
        );

      this.state.selection =
        nextSelection;

      const nextLimit =
        this.getFlavourLimit();

      if (
        nextLimit !== previousLimit
      ) {
        this.clearFlavours();
      } else if (
        this.state.selectedFlavours
          .length > nextLimit
      ) {
        this.state.selectedFlavours =
          this.state.selectedFlavours.slice(
            0,
            nextLimit
          );
      }

      this.renderFlavourSelection();

      this.renderExtras();

      this.renderAll();

      this.emitStateChange(
        'experience'
      );
    }

    setMinimumDate() {
      const input =
        this.elements.date;

      if (!input) {
        return;
      }

      const minimumDate =
        new Date();

      minimumDate.setHours(
        0,
        0,
        0,
        0
      );

      minimumDate.setDate(
        minimumDate.getDate() + 7
      );

      input.min =
        formatLocalDate(minimumDate);
    }

    updateStepButton(
      step,
      enabled
    ) {
      const button =
        this.booking.querySelector(
          `[data-step="${step}"] [data-next-step]`
        );

      if (button) {
        button.disabled = !enabled;
      }
    }

    getFlavourLimit() {
      return Math.max(
        asNumber(
          this.state.selection
            ?.flavoursIncluded,
          2
        ),
        0
      );
    }

    renderFlavours() {
      const grid =
        this.elements.flavourGrid;

      if (!grid) {
        return;
      }

      const fragment =
        document.createDocumentFragment();

      grid.replaceChildren();

      asArray(
        this.config.flavours
      ).forEach((flavour) => {
        const card =
          document.createElement(
            'button'
          );

        card.type = 'button';

        card.className =
          'db-booking__flavour-card';

        card.dataset.flavourId =
          flavour.id;

        card.setAttribute(
          'aria-pressed',
          'false'
        );

        card.append(
          this.createSpan(
            'db-booking__flavour-check',
            '✓',
            true
          ),

          this.createFlavourVisual(
            flavour
          ),

          this.createSpan(
            'db-booking__flavour-name',
            flavour.name || ''
          ),

          this.createSpan(
            'db-booking__flavour-price',
            asNumber(
              flavour.extraPrice,
              0
            ) > 0
              ? `+${this.pricing.formatCurrency(
                  flavour.extraPrice
                )}`
              : 'Included'
          )
        );

        card.addEventListener(
          'click',
          () =>
            this.toggleFlavour(
              flavour,
              card
            ),
          {
            signal: this.signal
          }
        );

        fragment.appendChild(card);
      });

      grid.appendChild(fragment);
    }

    createFlavourVisual(
      flavour
    ) {
      const visual =
        this.createSpan(
          'db-booking__flavour-visual',
          '',
          true
        );

      visual.style.setProperty(
        '--flavour-color',
        FLAVOUR_COLOURS[
          flavour.id
        ] || '#f4e9da'
      );

      return visual;
    }

    toggleFlavour(
      flavour,
      card
    ) {
      const index =
        this.state.selectedFlavours
          .findIndex(
            (item) =>
              item.id === flavour.id
          );

      if (index >= 0) {
        this.state.selectedFlavours
          .splice(index, 1);
      } else {
        const limit =
          this.getFlavourLimit();

        if (
          limit === 0 ||
          this.state.selectedFlavours
            .length >= limit
        ) {
          this.showFlavourLimitFeedback(
            card,
            limit
          );

          return;
        }

        this.state.selectedFlavours
          .push(flavour);
      }

      this.renderFlavourSelection();

      this.renderPricing();

      this.emitStateChange(
        'flavours'
      );
    }

    showFlavourLimitFeedback(
      card,
      limit
    ) {
      if (
        this.elements.flavourMessage
      ) {
        this.elements.flavourMessage
          .textContent =
          `Your experience includes up to ${limit} flavours.`;
      }

      if (
        typeof card.animate ===
        'function'
      ) {
        card.animate(
          [
            {
              transform:
                'translateX(0)'
            },
            {
              transform:
                'translateX(-5px)'
            },
            {
              transform:
                'translateX(5px)'
            },
            {
              transform:
                'translateX(0)'
            }
          ],
          {
            duration: 240,
            easing: 'ease'
          }
        );
      }
    }

    clearFlavours() {
      this.state.selectedFlavours =
        [];
    }

    renderFlavourSelection() {
      const selectedIds =
        new Set(
          this.state.selectedFlavours
            .map(
              (flavour) =>
                flavour.id
            )
        );

      this.booking
        .querySelectorAll(
          SELECTORS.flavourCard
        )
        .forEach((card) => {
          const selected =
            selectedIds.has(
              card.dataset.flavourId
            );

          card.classList.toggle(
            'is-selected',
            selected
          );

          card.setAttribute(
            'aria-pressed',
            String(selected)
          );
        });

      const limit =
        this.getFlavourLimit();

      const selectedCount =
        this.state.selectedFlavours
          .length;

      if (
        this.elements.flavourCount
      ) {
        this.elements.flavourCount
          .textContent =
          String(selectedCount);
      }

      if (
        this.elements.flavourLimit
      ) {
        this.elements.flavourLimit
          .textContent =
          String(limit);
      }

      if (
        this.elements.flavourContinue
      ) {
        this.elements.flavourContinue
          .disabled =
          limit === 0 ||
          selectedCount !== limit;
      }

      if (
        !this.elements.flavourMessage
      ) {
        return;
      }

      if (
        selectedCount === limit &&
        limit > 0
      ) {
        this.elements.flavourMessage
          .textContent =
          `Perfect! You selected all ${limit} flavours.`;

        return;
      }

      const remaining =
        Math.max(
          limit - selectedCount,
          0
        );

      this.elements.flavourMessage
        .textContent =
        `Choose ${remaining} more ${
          remaining === 1
            ? 'flavour'
            : 'flavours'
        }.`;
    }

    renderExtras() {
      const grid =
        this.elements.extrasGrid;

      if (!grid) {
        return;
      }

      const selectedIds =
        new Set(
          this.state.selectedExtras
            .map(
              (extra) =>
                extra.id
            )
        );

      const fragment =
        document.createDocumentFragment();

      grid.replaceChildren();

      asArray(
        this.config.extras
      ).forEach((extra) => {
        const card =
          document.createElement(
            'button'
          );

        const selected =
          selectedIds.has(extra.id);

        card.type = 'button';

        card.className =
          'db-booking__extra-card';

        card.dataset.extraId =
          extra.id;

        card.classList.toggle(
          'is-selected',
          selected
        );

        card.setAttribute(
          'aria-pressed',
          String(selected)
        );

        card.append(
          this.createSpan(
            'db-booking__extra-check',
            '✓',
            true
          ),

          this.createSpan(
            'db-booking__extra-name',
            extra.name || ''
          ),

          this.createSpan(
            'db-booking__extra-description',
            extra.description || ''
          ),

          this.createSpan(
            'db-booking__extra-price',
            this.getRenderedExtraPrice(
              extra
            )
          )
        );

        card.addEventListener(
          'click',
          () =>
            this.toggleExtra(extra),
          {
            signal: this.signal
          }
        );

        fragment.appendChild(card);
      });

      grid.appendChild(fragment);

      this.renderPricing();
    }

    toggleExtra(extra) {
      const index =
        this.state.selectedExtras
          .findIndex(
            (item) =>
              item.id === extra.id
          );

      if (index >= 0) {
        this.state.selectedExtras
          .splice(index, 1);
      } else {
        this.state.selectedExtras
          .push(extra);
      }

      this.renderExtraSelection();

      this.renderPricing();

      this.emitStateChange(
        'extras'
      );
    }

    renderExtraSelection() {
      const selectedIds =
        new Set(
          this.state.selectedExtras
            .map(
              (extra) =>
                extra.id
            )
        );

      this.booking
        .querySelectorAll(
          SELECTORS.extraCard
        )
        .forEach((card) => {
          const selected =
            selectedIds.has(
              card.dataset.extraId
            );

          card.classList.toggle(
            'is-selected',
            selected
          );

          card.setAttribute(
            'aria-pressed',
            String(selected)
          );
        });
    }

    getRenderedExtraPrice(
      extra
    ) {
      if (
        extra.pricingType ===
        'perGuest'
      ) {
        const total =
          this.pricing.getExtraPrice(
            extra,
            this.state.guests
          );

        return (
          `${this.pricing.formatCurrency(
            extra.price
          )} per guest · ` +
          `${this.pricing.formatCurrency(
            total,
            total % 1 !== 0
          )} total`
        );
      }

      return (
        `+${this.pricing.formatCurrency(
          extra.price
        )}`
      );
    }

    getSelectedPackage() {
      const selection =
        this.state.selection || {};

      return {
        id: selection.id,

        name: selection.name,

        price: asNumber(
          selection.price,
          0
        ),

        flavoursIncluded:
          this.getFlavourLimit()
      };
    }

    calculateTotals() {
      return this.pricing
        .calculateTotals({
          selectedPackage:
            this.getSelectedPackage(),

          selectedFlavours:
            this.state
              .selectedFlavours,

          selectedExtras:
            this.state
              .selectedExtras,

          guests:
            this.state.guests,

          travelFee: 0
        });
    }

    renderPricing() {
      const totals =
        this.calculateTotals();

      if (
        this.elements.extrasTotal
      ) {
        this.elements.extrasTotal
          .textContent =
          `Extras total: ${this.pricing.formatCurrency(
            totals.extrasTotal,
            totals.extrasTotal % 1 !== 0
          )}`;
      }
    }

    renderAll() {
      this.renderFlavourSelection();

      this.renderExtraSelection();

      this.renderPricing();
    }

    restoreSelectionsFromDom() {
      this.state.selectedFlavours =
        Array.from(
          this.booking.querySelectorAll(
            `${SELECTORS.flavourCard}.is-selected`
          )
        )
          .map((card) =>
            asArray(
              this.config.flavours
            ).find(
              (flavour) =>
                flavour.id ===
                card.dataset.flavourId
            )
          )
          .filter(Boolean);

      this.state.selectedExtras =
        Array.from(
          this.booking.querySelectorAll(
            `${SELECTORS.extraCard}.is-selected`
          )
        )
          .map((card) =>
            asArray(
              this.config.extras
            ).find(
              (extra) =>
                extra.id ===
                card.dataset.extraId
            )
          )
          .filter(Boolean);
    }

    readCustomer() {
      return {
        firstName:
          this.elements
            ?.firstName
            ?.value
            .trim() || '',

        lastName:
          this.elements
            ?.lastName
            ?.value
            .trim() || '',

        email:
          this.elements
            ?.email
            ?.value
            .trim() || '',

        phone:
          this.elements
            ?.phone
            ?.value
            .trim() || '',

        notes:
          this.elements
            ?.notes
            ?.value
            .trim() || ''
      };
    }

    createSpan(
      className,
      text,
      hidden = false
    ) {
      const span =
        document.createElement(
          'span'
        );

      span.className =
        className;

      span.textContent =
        text;

      if (hidden) {
        span.setAttribute(
          'aria-hidden',
          'true'
        );
      }

      return span;
    }

    emit(name, detail) {
      document.dispatchEvent(
        new CustomEvent(
          name,
          {
            detail
          }
        )
      );
    }

    emitStateChange(source) {
      const detail = {
        source,

        state:
          this.getPublicState(),

        totals:
          this.calculateTotals()
      };

      window.DOLOBITES_V3_BOOKING_STATE =
        detail.state;

      this.emit(
        'dolobites:booking-change',
        detail
      );
    }

    getPublicState() {
      return {
        currentStep:
          this.state.currentStep,

        date:
          this.state.date,

        eventType:
          this.state.eventType,

        location:
          this.state.location,

        guests:
          this.state.guests,

        selection: {
          ...this.state.selection
        },

        selectedFlavours: [
          ...this.state
            .selectedFlavours
        ],

        selectedExtras: [
          ...this.state
            .selectedExtras
        ],

        customer: {
          ...this.state.customer
        }
      };
    }
  }

  function initializeDolobitesBookingV3(
    root = document
  ) {
    const bookings =
      Array.from(
        root.querySelectorAll(
          SELECTORS.booking
        )
      );

    bookings.forEach(
      (booking) => {
        if (
          booking.dataset
            .initialized === 'true'
        ) {
          return;
        }

        booking.dataset.initialized =
          'true';

        const instance =
          new DolobitesBookingV3(
            booking
          );

        booking.dolobitesBookingV3 =
          instance;

        instance.init();
      }
    );
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initializeDolobitesBookingV3();
      }
    );
  } else {
    initializeDolobitesBookingV3();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeDolobitesBookingV3(
        event.target || document
      );
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      event.target
        ?.querySelectorAll?.(
          SELECTORS.booking
        )
        .forEach(
          (booking) =>
            booking
              .dolobitesBookingV3
              ?.destroy()
        );
    }
  );
})();